// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import moment from 'moment';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'office-ui-fabric-react/lib/Modal';

import {
    ComboBox,
    IDropdownOption,
    IDropdownStyles,
    PrimaryButton,
} from 'office-ui-fabric-react';

import i18n from './../../lib/i18n';
import supportedLocales from 'src/constants/supportedLocales';

import Styles from './styles.module.scss';
import { logPageAction } from './../../lib/ApplicationInsights';
import { sharedComboBoxOptionStyles } from 'src/helpers/sharedComboBoxOptionStyles';

interface IModalProps {
    showModal: boolean;
    closeModal: any;
    disabled?: boolean;
}

const dropdownStyles: Partial<IDropdownStyles> = {
    dropdownItems: {
        maxHeight: '200px',
    },
    callout: {
        marginLeft: '2px',
    },
};

function translateLocaleCode(localeCode: string) {
    // localize language
    const { t } = useTranslation();
    return t(`locale-${localeCode}`);
}

function translateCountryCode(countryCode: string) {
    // localize country code
    // return empty string if unable to localize
    const { t } = useTranslation();
    const countryLocalized = t(`country-${countryCode.toLowerCase()}`);
    return countryLocalized.includes('country-') ? '' : `(${countryLocalized})`;
}

function translateLanguageOptions(hideCountry: boolean = false): IDropdownOption[] {
    return supportedLocales
        .map((key: string): IDropdownOption => {
            const splitCode = key.split('-');
            const localeCode = splitCode[0] || '';
            const countryCode = splitCode.length > 1 && splitCode[1] || '';
            let text = `${translateLocaleCode(localeCode)}`;
            if (!hideCountry) {
                text += ` ${translateCountryCode(countryCode)}`;
            }
            return { key, text };
        })
        .sort((a, b) => (a.text.localeCompare(b.text)));
}

export default function LanguageSelectorModal(props: IModalProps) {
    const { t } = useTranslation();
    const { showModal, closeModal, disabled } = props;

    const [language, setLanguage] = React.useState(i18n.language);
    const [showError, updateShowError] = React.useState(false);

    const options: IDropdownOption[] = translateLanguageOptions();

    function onChange(
        event: any,
        selectedOption?: IDropdownOption
    ) {
        if (selectedOption) {
            logPageAction('Language selected', { itemSelected: selectedOption.text });
            setLanguage(`${selectedOption.key}`);
        }
    }

    function onClose() {
        updateShowError(false);
        closeModal();
    }

    function onComboBoxFocus() {
        document.getElementsByClassName('ms-ComboBox')[0].setAttribute('style', 'border-color:black;outline-style:auto;outline-offset:3px;outline-width:3px');
        let comboValue = document.getElementsByClassName('ms-ComboBox-Input')[0].getAttribute('value');
        document.getElementsByClassName('ms-ComboBox-Input')[0].setAttribute('aria-label', comboValue ? comboValue : t('language_description'));
    }

    function onComboBoxBlur() {
        document.getElementsByClassName('ms-ComboBox')[0].setAttribute('style', 'outline-style:none');
        let comboValue = document.getElementsByClassName('ms-ComboBox-Input')[0].getAttribute('value');
        document.getElementsByClassName('ms-ComboBox-Input')[0].setAttribute('aria-label', comboValue ? comboValue : t('language_description'));
    }

    const onSave = () => {
        logPageAction('Language saved');

        i18n.changeLanguage(language, (err) => {
            // There are six possible translation files for each language
            // Render error message if all six scenarios fail. ways = 2 * number of files
            if (err && Array.isArray(err) && err.length === 6) {
                updateShowError(true);
            } else {
                moment.locale(language);
                onClose();
            }
        });
    };

    return (
        <Modal
            isOpen={showModal}
            onDismiss={closeModal}
            isBlocking={false}
            containerClassName={Styles.container}
            titleAriaId="language_id"
            subtitleAriaId="language_description_id"
        >
            <div className={Styles.header} id="language_id">
                {t('language')}
            </div>
            <div className={Styles.headerDesc} id="language_description_id">
                {t('language_description')}
            </div>
            <ComboBox
                defaultSelectedKey={language}
                selectedKey={language}
                className={Styles.dropdown}
                onFocus={onComboBoxFocus}
                onBlur={onComboBoxBlur}
                styles={dropdownStyles}
                comboBoxOptionStyles={sharedComboBoxOptionStyles}
                options={options}
                onChange={onChange}
                errorMessage={showError ? t('language_error') : ''}
            />
            <div className={Styles.buttons}>
                <PrimaryButton
                    allowDisabledFocus={true}
                    text={t('cancel')}
                    onClick={closeModal}
                    className={Styles.cancel}
                />
                <PrimaryButton
                    allowDisabledFocus={true}
                    disabled={disabled}
                    text={t('save')}
                    onClick={onSave}
                    className={Styles.save}
                />
            </div>
        </Modal>
    );
}
